import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h1`
  color: #333;
  font-size: 1.5rem;
  margin: 0;
`;

const Controls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const Select = styled.select`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Input = styled.input`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.875rem;
  
  &:focus {
    outline: none;
    border-color: #667eea;
  }
`;

const Button = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.primary && `
    background: #667eea;
    color: white;
    &:hover { background: #5a6fd6; }
  `}
  
  ${props => props.danger && `
    background: #dc3545;
    color: white;
    &:hover { background: #c82333; }
  `}
  
  ${props => props.warning && `
    background: #ffc107;
    color: #333;
    &:hover { background: #e0a800; }
  `}
  
  ${props => props.secondary && `
    background: #6c757d;
    color: white;
    &:hover { background: #5a6268; }
  `}
  
  ${props => props.success && `
    background: #28a745;
    color: white;
    &:hover { background: #218838; }
  `}
  
  ${props => props.disabled && `
    opacity: 0.6;
    cursor: not-allowed;
  `}
`;

const ToggleSwitch = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  cursor: pointer;
  user-select: none;
`;

const Switch = styled.div`
  width: 50px;
  height: 26px;
  background: ${props => props.active ? '#28a745' : '#ccc'};
  border-radius: 13px;
  position: relative;
  transition: all 0.3s;
  
  &::after {
    content: '';
    position: absolute;
    width: 22px;
    height: 22px;
    background: white;
    border-radius: 50%;
    top: 2px;
    left: ${props => props.active ? '26px' : '2px'};
    transition: all 0.3s;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${props => props.active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.active ? '#155724' : '#721c24'};
`;

const StatsCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled.div`
  background: white;
  padding: 1rem;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${props => props.color || '#333'};
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

const LogTable = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 160px 80px 100px 1fr 120px;
  gap: 0.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  font-weight: 600;
  font-size: 0.875rem;
  color: #666;
  
  @media (max-width: 900px) {
    display: none;
  }
`;

const LogRow = styled.div`
  display: grid;
  grid-template-columns: 160px 80px 100px 1fr 120px;
  gap: 0.5rem;
  padding: 1rem;
  border-bottom: 1px solid #f0f0f0;
  align-items: center;
  font-size: 0.875rem;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const LevelBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
  
  ${props => props.level === 'error' && `
    background: #f8d7da;
    color: #721c24;
  `}
  ${props => props.level === 'warn' && `
    background: #fff3cd;
    color: #856404;
  `}
  ${props => props.level === 'info' && `
    background: #cfe2ff;
    color: #084298;
  `}
  ${props => props.level === 'debug' && `
    background: #e2e3e5;
    color: #41464b;
  `}
`;

const CategoryBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  background: #e9ecef;
  color: #495057;
`;

const MessageText = styled.div`
  color: #333;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  
  @media (max-width: 900px) {
    white-space: normal;
  }
`;

const TimeText = styled.div`
  color: #666;
  font-size: 0.8rem;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid #dee2e6;
`;

const PageInfo = styled.div`
  font-size: 0.875rem;
  color: #666;
`;

const PageButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 500px;
  width: 90%;
`;

const ModalTitle = styled.h3`
  margin-top: 0;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
`;

const Toast = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  color: white;
  font-weight: 500;
  z-index: 1001;
  animation: slideIn 0.3s ease;
  
  ${props => props.type === 'success' && `background: #28a745;`}
  ${props => props.type === 'error' && `background: #dc3545;`}
  
  @keyframes slideIn {
    from { transform: translateX(100%); opacity: 0; }
    to { transform: translateX(0); opacity: 1; }
  }
`;

const LogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({ info: 0, warn: 0, error: 0, debug: 0, total: 0 });
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // 分页
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  
  // 筛选
  const [level, setLevel] = useState('all');
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  
  // 模态框
  const [showCleanModal, setShowCleanModal] = useState(false);
  const [cleanDays, setCleanDays] = useState(30);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Toast
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchStats();
    fetchLogs();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, level, category, search]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/logs/status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEnabled(response.data.enabled);
    } catch (error) {
      console.error('获取日志状态失败:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get('/api/admin/logs/stats', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data.stats);
    } catch (error) {
      console.error('获取日志统计失败:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = { page, pageSize };
      if (level !== 'all') params.level = level;
      if (category !== 'all') params.category = category;
      if (search) params.search = search;

      const response = await axios.get('/api/admin/logs', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      setLogs(response.data.logs);
      setTotal(response.data.total);
      setTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('获取日志列表失败:', error);
      showToast('获取日志列表失败', 'error');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  };

  const toggleLogging = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const endpoint = enabled ? '/api/admin/logs/disable' : '/api/admin/logs/enable';
      
      const response = await axios.post(endpoint, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEnabled(response.data.enabled);
      showToast(response.data.message);
    } catch (error) {
      console.error('切换日志状态失败:', error);
      showToast('操作失败', 'error');
    }
  };

  const handleClean = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete('/api/admin/logs/clean', {
        headers: { Authorization: `Bearer ${token}` },
        data: { days: cleanDays }
      });
      
      showToast(response.data.message);
      setShowCleanModal(false);
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('清理日志失败:', error);
      showToast('清理失败', 'error');
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('确定要清空所有日志吗？此操作不可恢复！')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.delete('/api/admin/logs/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      showToast(response.data.message);
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error('清空日志失败:', error);
      showToast('清空失败', 'error');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const params = {};
      if (level !== 'all') params.level = level;
      if (category !== 'all') params.category = category;

      const response = await axios.get('/api/admin/logs/export', {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `logs_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      showToast('日志导出成功');
    } catch (error) {
      console.error('导出日志失败:', error);
      showToast('导出失败', 'error');
    }
  };

  const viewDetail = (log) => {
    setSelectedLog(log);
    setShowDetailModal(true);
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      api: 'API',
      database: '数据库',
      auth: '认证',
      service: '服务',
      chat: '客服',
      system: '系统',
      admin: '管理',
      other: '其他'
    };
    return labels[cat] || cat;
  };

  return (
    <AdminLayout title="日志管理">
      <Container>
        <Header>
          <Title>日志管理</Title>
          <Controls>
            <ToggleSwitch onClick={toggleLogging}>
              <Switch active={enabled} />
              <StatusBadge active={enabled}>
                {enabled ? '● 记录中' : '○ 已停止'}
              </StatusBadge>
            </ToggleSwitch>
          </Controls>
        </Header>

        <StatsCards>
          <StatCard>
            <StatValue color="#084298">{stats.total}</StatValue>
            <StatLabel>总日志</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#084298">{stats.info}</StatValue>
            <StatLabel>信息</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#856404">{stats.warn}</StatValue>
            <StatLabel>警告</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue color="#721c24">{stats.error}</StatValue>
            <StatLabel>错误</StatLabel>
          </StatCard>
        </StatsCards>

        <Header style={{ marginTop: '1.5rem' }}>
          <Controls>
            <ControlGroup>
              <Select value={level} onChange={(e) => { setLevel(e.target.value); setPage(1); }}>
                <option value="all">全部级别</option>
                <option value="info">信息</option>
                <option value="warn">警告</option>
                <option value="error">错误</option>
                <option value="debug">调试</option>
              </Select>
            </ControlGroup>
            <ControlGroup>
              <Select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }}>
                <option value="all">全部分类</option>
                <option value="api">API</option>
                <option value="database">数据库</option>
                <option value="auth">认证</option>
                <option value="service">服务</option>
                <option value="chat">客服</option>
                <option value="admin">管理</option>
                <option value="system">系统</option>
                <option value="other">其他</option>
              </Select>
            </ControlGroup>
            <ControlGroup>
              <Input 
                placeholder="搜索日志内容..." 
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                onKeyPress={(e) => e.key === 'Enter' && fetchLogs()}
              />
            </ControlGroup>
            <Button primary onClick={fetchLogs} disabled={loading}>
              🔍 搜索
            </Button>
          </Controls>
          <Controls>
            <Button success onClick={handleExport} disabled={loading}>
              📥 导出
            </Button>
            <Button warning onClick={() => setShowCleanModal(true)} disabled={loading}>
              🧹 清理
            </Button>
            <Button danger onClick={handleClearAll} disabled={loading}>
              🗑️ 清空
            </Button>
          </Controls>
        </Header>

        <LogTable>
          <TableHeader>
            <div>时间</div>
            <div>级别</div>
            <div>分类</div>
            <div>消息</div>
            <div>操作</div>
          </TableHeader>

          {initialLoading ? (
            <EmptyState>加载中...</EmptyState>
          ) : logs.length === 0 ? (
            <EmptyState>
              <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📋</div>
              <div>暂无日志记录</div>
            </EmptyState>
          ) : (
            logs.map((log) => (
              <LogRow key={log._id}>
                <TimeText>{formatTime(log.createdAt)}</TimeText>
                <div><LevelBadge level={log.level}>{log.level.toUpperCase()}</LevelBadge></div>
                <div><CategoryBadge>{getCategoryLabel(log.category)}</CategoryBadge></div>
                <MessageText title={log.message}>{log.message}</MessageText>
                <div>
                  <Button secondary style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }} onClick={() => viewDetail(log)}>
                    详情
                  </Button>
                </div>
              </LogRow>
            ))
          )}

          <Pagination>
            <PageInfo>
              共 {total} 条记录，第 {page}/{totalPages} 页
            </PageInfo>
            <PageButtons>
              <Button secondary disabled={page <= 1} onClick={() => setPage(1)}>首页</Button>
              <Button secondary disabled={page <= 1} onClick={() => setPage(p => p - 1)}>上一页</Button>
              <Button secondary disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>下一页</Button>
              <Button secondary disabled={page >= totalPages} onClick={() => setPage(totalPages)}>末页</Button>
            </PageButtons>
          </Pagination>
        </LogTable>

        {/* 清理日志模态框 */}
        {showCleanModal && (
          <Modal onClick={() => setShowCleanModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalTitle>清理日志</ModalTitle>
              <p style={{ color: '#666' }}>选择要清理多少天之前的日志：</p>
              <Select value={cleanDays} onChange={(e) => setCleanDays(parseInt(e.target.value))}>
                <option value={7}>7天前</option>
                <option value={14}>14天前</option>
                <option value={30}>30天前</option>
                <option value={60}>60天前</option>
                <option value={90}>90天前</option>
              </Select>
              <ModalButtons>
                <Button secondary onClick={() => setShowCleanModal(false)}>取消</Button>
                <Button primary onClick={handleClean}>确认清理</Button>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}

        {/* 日志详情模态框 */}
        {showDetailModal && selectedLog && (
          <Modal onClick={() => setShowDetailModal(false)}>
            <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '700px' }}>
              <ModalTitle>日志详情</ModalTitle>
              <div style={{ marginTop: '1rem' }}>
                <p><strong>时间：</strong>{formatTime(selectedLog.createdAt)}</p>
                <p><strong>级别：</strong><LevelBadge level={selectedLog.level}>{selectedLog.level.toUpperCase()}</LevelBadge></p>
                <p><strong>分类：</strong><CategoryBadge>{getCategoryLabel(selectedLog.category)}</CategoryBadge></p>
                <p><strong>来源：</strong>{selectedLog.source}</p>
                <p><strong>消息：</strong></p>
                <pre style={{ background: '#f5f5f5', padding: '1rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px' }}>
                  {selectedLog.message}
                </pre>
                {selectedLog.ip && <p><strong>IP地址：</strong>{selectedLog.ip}</p>}
                {selectedLog.method && <p><strong>请求方法：</strong>{selectedLog.method}</p>}
                {selectedLog.path && <p><strong>请求路径：</strong>{selectedLog.path}</p>}
                {selectedLog.statusCode && <p><strong>状态码：</strong>{selectedLog.statusCode}</p>}
                {selectedLog.duration && <p><strong>耗时：</strong>{selectedLog.duration}ms</p>}
                {selectedLog.userId && (
                  <p><strong>用户：</strong>{selectedLog.userId.username || selectedLog.userId.email}</p>
                )}
                {selectedLog.adminId && (
                  <p><strong>管理员：</strong>{selectedLog.adminId.username}</p>
                )}
                {selectedLog.error && (
                  <div>
                    <p><strong>错误信息：</strong></p>
                    <pre style={{ background: '#fff3cd', padding: '1rem', borderRadius: '4px', overflow: 'auto', maxHeight: '200px', fontSize: '0.8rem' }}>
                      {selectedLog.error.message}
                      {selectedLog.error.stack && `\n\n${selectedLog.error.stack}`}
                    </pre>
                  </div>
                )}
              </div>
              <ModalButtons>
                <Button secondary onClick={() => setShowDetailModal(false)}>关闭</Button>
              </ModalButtons>
            </ModalContent>
          </Modal>
        )}

        {toast && <Toast type={toast.type}>{toast.message}</Toast>}
      </Container>
    </AdminLayout>
  );
};

export default LogsPage;
